/**
 * Barra de filtro estilo Kommo: chip de visão + busca + resumo à direita.
 * O painel do chip reúne as visões (esquerda) e os filtros avançados (direita);
 * filtros ativos aparecem como chips removíveis dentro da barra.
 */

import { ReactNode, useEffect, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PipelineType } from '../types';

export type PipelineBoardView = 'ativos' | 'venda' | 'fixados' | 'perdidos' | 'todos';

export interface PipelineActiveFilter {
  key: string;
  label: string;
  onRemove: () => void;
}

/** Portais do antd (dropdowns dos selects do painel) não contam como clique fora */
const PANEL_PORTAL_SELECTORS = '.ant-select-dropdown, .ant-picker-dropdown';

export interface PipelineFilterSummary {
  count: number;
  value: number;
}

interface PipelineFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  view: PipelineBoardView;
  onViewChange: (view: PipelineBoardView) => void;
  summary?: PipelineFilterSummary | null;
  /** Tipo da pipeline ativa — define quais visões aparecem no seletor */
  pipelineTipo?: PipelineType | string | null;
  /** Campos de filtro exibidos à direita no painel */
  filters?: ReactNode;
  activeFilters?: PipelineActiveFilter[];
  onClearFilters?: () => void;
  className?: string;
}

const VIEW_LABELS: Record<PipelineBoardView, string> = {
  ativos: 'Leads ativos',
  venda: 'Venda',
  fixados: 'Fixados',
  perdidos: 'Perdidos',
  todos: 'Todos os cards',
};

const VIEWS_POS_VENDA: PipelineBoardView[] = ['ativos', 'fixados', 'perdidos', 'todos'];
const VIEWS_QUALIFICACAO: PipelineBoardView[] = ['ativos', 'venda', 'perdidos', 'todos'];
const VIEWS_COMERCIAL: PipelineBoardView[] = ['ativos', 'perdidos', 'todos'];

export function getViewsForPipelineTipo(tipo?: PipelineType | string | null): PipelineBoardView[] {
  if (tipo === 'pos_venda') return VIEWS_POS_VENDA;
  if (tipo === 'qualificacao') return VIEWS_QUALIFICACAO;
  return VIEWS_COMERCIAL;
}

export function PipelineFilterBar({
  search,
  onSearchChange,
  view,
  onViewChange,
  summary,
  pipelineTipo,
  filters,
  activeFilters = [],
  onClearFilters,
  className,
}: PipelineFilterBarProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const views = getViewsForPipelineTipo(pipelineTipo);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (rootRef.current?.contains(target)) return;
      if (target.closest(PANEL_PORTAL_SELECTORS)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Se a visão atual não existir nesta pipeline (ex.: Fixados na Comercial), volta para ativos
  useEffect(() => {
    if (!views.includes(view)) {
      onViewChange('ativos');
    }
  }, [views, view, onViewChange]);

  const formattedValue =
    summary != null
      ? summary.value.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          maximumFractionDigits: 0,
        })
      : null;

  const chipView = views.includes(view) ? view : 'ativos';

  return (
    <div ref={rootRef} className={cn('relative flex-1 min-w-0', className)}>
      <div className="flex items-center gap-4">
        <div
          className={cn(
            'flex flex-1 items-center gap-2 min-w-0 h-9 px-2 rounded-md border border-gray-200 dark:border-zinc-700 overflow-hidden',
            'bg-white dark:bg-zinc-900 focus-within:border-[#FF3737] focus-within:ring-1 focus-within:ring-[#FF3737]/30'
          )}
        >
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1 shrink-0 h-6 px-2 rounded text-xs font-semibold',
              'bg-emerald-50 text-emerald-700 border border-emerald-200',
              'dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800',
              'hover:bg-emerald-100 dark:hover:bg-emerald-950/60 transition-colors'
            )}
          >
            {VIEW_LABELS[chipView]}
            {activeFilters.length > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-emerald-600 text-white text-[10px] leading-none">
                {activeFilters.length}
              </span>
            )}
            <ChevronDown className={cn('w-3 h-3 transition-transform', open && 'rotate-180')} />
          </button>

          {activeFilters.map((filter) => (
            <span
              key={filter.key}
              title={filter.label}
              className={cn(
                'inline-flex items-center gap-1 shrink-0 max-w-48 h-6 pl-2 pr-1 rounded text-xs',
                'bg-gray-100 text-gray-700 border border-gray-200',
                'dark:bg-zinc-800 dark:text-gray-200 dark:border-zinc-700'
              )}
            >
              <span className="truncate">{filter.label}</span>
              <button
                type="button"
                onClick={filter.onRemove}
                aria-label={`Remover filtro ${filter.label}`}
                className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200 dark:hover:text-gray-100 dark:hover:bg-zinc-700"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}

          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Pesquisar e filtrar"
              className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm text-gray-700 dark:text-gray-200 placeholder:text-gray-400"
            />
          </div>
        </div>

        {summary != null && (
          <div className="shrink-0 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
            <span>{summary.count} {summary.count === 1 ? 'lead' : 'leads'}:</span>{' '}
            <span className="font-medium text-gray-800 dark:text-gray-100">{formattedValue}</span>
          </div>
        )}
      </div>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 flex max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg">
          <div
            className={cn(
              'w-56 shrink-0 py-1',
              filters && 'border-r border-gray-200 dark:border-zinc-700'
            )}
          >
            {views.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  onViewChange(option);
                  // Com filtros no painel, mantém aberto para combinar visão + filtros
                  if (!filters) setOpen(false);
                }}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm transition-colors',
                  option === chipView
                    ? 'text-[#FF3737] dark:text-[#FF3737] font-semibold bg-[#FF3737]/10 dark:bg-[#FF3737]/10 border-l-2 border-[#FF3737]'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800 border-l-2 border-transparent'
                )}
              >
                {VIEW_LABELS[option]}
              </button>
            ))}
          </div>

          {filters && (
            <div className="w-[420px] min-w-0 p-3 flex flex-col gap-3">
              {filters}
              {onClearFilters && (
                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={onClearFilters}
                    disabled={activeFilters.length === 0}
                    className="text-xs font-medium text-gray-500 hover:text-[#FF3737] disabled:opacity-40 disabled:hover:text-gray-500 disabled:cursor-not-allowed"
                  >
                    Limpar filtros
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PipelineFilterBar;

/** Etapas de resultado (fora do funil ativo) */
export function isOutcomeStageName(nome: string, oculta?: boolean): boolean {
  if (oculta) return true;
  const n = nome.toLowerCase();
  return (
    n.includes('perdido') ||
    n.includes('perda') ||
    n.includes('fixado')
  );
}
